import { useRef, useState, useCallback, useLayoutEffect } from 'react';

/* Живая drag-and-drop сортировка списка на pointer-событиях (без нативного
   HTML5-drag, поэтому нет браузерного полупрозрачного «призрака»).

   Поведение как в обычных сортируемых списках:
   - тащить можно только за ручку (getHandleProps);
   - взятый элемент следует за курсором (приподнят, с тенью);
   - соседи в реальном времени разъезжаются и встают на место (FLIP), без пустых дырок;
   - на отпускании элемент плавно садится в выбранную позицию и порядок сохраняется.

   items     — массив объектов с .id в текущем порядке (управляется родителем);
   onReorder(ids) — применить новый порядок (вызывается вживую при перетаскивании);
   onCommit(ids)  — сохранить порядок на бэкенд (вызывается при отпускании). */
export default function useDragSort(items, onReorder, onCommit) {
  const [draggingId, setDraggingId] = useState(null);

  const grab = useRef(null);          // { id, offsetY } — что тащим и где схватили
  const lastY = useRef(0);            // текущая Y курсора
  const changed = useRef(false);      // менялся ли порядок
  const orderRef = useRef([]);
  orderRef.current = items.map(it => it.id);

  const nodes = useRef(new Map());      // id -> DOM
  const prevRects = useRef(new Map());  // id -> позиция на прошлом рендере
  const refCbs = useRef(new Map());

  // Свежие колбэки родителя (их идентичность меняется между рендерами)
  const onReorderRef = useRef(onReorder); onReorderRef.current = onReorder;
  const onCommitRef = useRef(onCommit); onCommitRef.current = onCommit;

  // Приклеиваем взятый элемент под курсор
  const positionDragged = useCallback(() => {
    const g = grab.current; if (!g) return;
    const el = nodes.current.get(g.id); if (!el) return;
    el.style.transition = 'none';
    el.style.transform = 'none';
    const top = el.getBoundingClientRect().top;
    el.style.transform = `translateY(${Math.round(lastY.current - g.offsetY - top)}px)`;
    el.style.zIndex = '50';
    el.style.position = 'relative';
    el.style.pointerEvents = 'none';
    el.style.boxShadow = '0 12px 32px -10px rgba(0,0,0,0.40)';
    el.style.opacity = '1';
  }, []);

  // FLIP-анимация соседей; взятым управляем вручную
  useLayoutEffect(() => {
    const dragging = grab.current?.id;
    nodes.current.forEach((el, id) => {
      if (!el) return;
      if (id === dragging) { prevRects.current.set(id, el.getBoundingClientRect()); positionDragged(); return; }
      const nr = el.getBoundingClientRect();
      const pr = prevRects.current.get(id);
      prevRects.current.set(id, nr);
      if (!pr) return;
      const dx = pr.left - nr.left, dy = pr.top - nr.top;
      if (!dx && !dy) return;
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      requestAnimationFrame(() => {
        el.style.transition = 'transform 180ms cubic-bezier(0.2, 0, 0, 1)';
        el.style.transform = '';
      });
    });
  });

  const getRef = (id) => {
    let cb = refCbs.current.get(id);
    if (!cb) {
      cb = (el) => {
        if (el) nodes.current.set(id, el);
        else { nodes.current.delete(id); prevRects.current.delete(id); }
      };
      refCbs.current.set(id, cb);
    }
    return cb;
  };

  // Куда вставить взятый элемент — считаем по серединам соседей
  const reorderLive = useCallback(() => {
    const g = grab.current; if (!g) return;
    const order = orderRef.current;
    const without = order.filter(x => x !== g.id);
    let insertAt = without.length;
    for (let i = 0; i < without.length; i++) {
      const el = nodes.current.get(without[i]); if (!el) continue;
      const r = el.getBoundingClientRect();
      if (lastY.current < r.top + r.height / 2) { insertAt = i; break; }
    }
    const next = without.slice();
    next.splice(insertAt, 0, g.id);
    if (next.length === order.length && next.every((v, i) => v === order[i])) return;
    orderRef.current = next;
    changed.current = true;
    onReorderRef.current?.(next);
  }, []);

  const onMove = useCallback((e) => {
    if (!grab.current) return;
    lastY.current = e.clientY;
    reorderLive();
    positionDragged();
  }, [reorderLive, positionDragged]);

  const endDrag = useCallback(() => {
    const g = grab.current;
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', endDrag);
    document.removeEventListener('pointercancel', endDrag);
    document.body.style.userSelect = '';
    if (g) {
      const el = nodes.current.get(g.id);
      prevRects.current.delete(g.id); // чтобы FLIP не дёрнул его повторно
      if (el) {
        el.style.transition = 'transform 180ms cubic-bezier(0.2, 0, 0, 1)';
        el.style.transform = '';
        const clear = () => {
          el.style.transition = ''; el.style.zIndex = ''; el.style.position = '';
          el.style.pointerEvents = ''; el.style.boxShadow = ''; el.style.opacity = '';
          el.removeEventListener('transitionend', clear);
        };
        el.addEventListener('transitionend', clear);
        setTimeout(clear, 260);
      }
    }
    if (changed.current) onCommitRef.current?.(orderRef.current);
    grab.current = null;
    changed.current = false;
    setDraggingId(null);
  }, [onMove]);

  const getHandleProps = (id) => ({
    onPointerDown: (e) => {
      if (e.button != null && e.button !== 0) return; // только ЛКМ/тач
      e.stopPropagation();
      e.preventDefault();
      const el = nodes.current.get(id);
      const top = el ? el.getBoundingClientRect().top : e.clientY;
      grab.current = { id, offsetY: e.clientY - top };
      lastY.current = e.clientY;
      changed.current = false;
      document.body.style.userSelect = 'none';
      setDraggingId(id);
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', endDrag);
      document.addEventListener('pointercancel', endDrag);
      requestAnimationFrame(positionDragged);
    },
    onClick: (e) => { e.stopPropagation(); e.preventDefault(); },
    style: { touchAction: 'none' },
  });

  const getItemProps = (id) => ({
    ref: getRef(id),
    'data-dragging': draggingId === id ? 'true' : undefined,
  });

  return { getHandleProps, getItemProps };
}
